/**
 * @author NikolaV
 */
import { Cache } from '@shared/Cache';
import { Auth } from '@shared/Auth';
import axios, { Method } from 'axios';


export interface IAjaxOptions extends JQueryAjaxSettings {
    url: string; // url without kuura.getContextPath
    method?: string; // 'get' || 'post'
    data?: any; // regular jquery ajax data field
    processData?: boolean;
    useToken?: boolean;
    cache?: boolean;
    cacheGroup?: string;
    cacheTimeoutS?: number;
    onProgress?: any;
    filesize?: number;

    // todo, not ajax related, should be removed, maybe not used
    modelId?: number;
    listLoadingModels?: number[];
}


export interface ITreeAjaxOptions extends IAjaxOptions {
    filesize?: number;
    modelId?: number;
    listLoadingModels?: any[];
    layerCounts?: number;
    ignoreLoading?: boolean
}


/**
 * Why do we need this? Wrapper over $.ajax that provides single fail handler, fail logging, caching
 *
 * kuura.isValidResponse() forces us to have one fail state handler copy pasted into two places
 *   inside if(!kuura.isValidResponse) { // fail handle }
 *   and also inside .fail( // fail handle )
 *
 * This fixes that, no need to ever call isValidResponse()
 *    fail state is handled inside .fail() and will be called when !isValidResponse
 *
 * !isValidResponse is considered a failed response, but since server returns 200 OK (satus: false),
 *    the browser wont log failed responses into console making them hard to detect.
 *    This wrapper will log thsoe requests into console
 *
 * Usage:
 *        Ajax(opts: AjaxOptions)
 *            .done()
 *            .fail()
 *            .always()
 */
export function Ajax(opts: IAjaxOptions): JQueryPromise<any> {
    let {
        data = {},
        method = 'GET',
        processData = true,
        cache = false,
        cacheGroup = '',
        cacheTimeoutS = Cache.defaults.timeout
    } = opts;

    if (!(data instanceof FormData)) {
        if (typeof data === 'object' && typeof data.projectId === 'undefined') {
            data.projectId = kuura.getProjectId();
            data.projectUuid = kuura.getProjectUuid();
        }

        if (processData === false) {
            data = JSON.stringify(data);
        }
    }

    const url = kuura.getContextPath() + (opts.url.startsWith('/') ? '' : '/') + opts.url;
    method = method.toLowerCase();

    // try cache
    const cacheKey = makeCacheKey(url, data);

    if (cache && method === 'get') {
        const cached = Cache.get(cacheKey, cacheGroup);
        if (cached) {
            const promise = $.Deferred().resolve(cached).promise();
            (promise as any).abort = () => {};
            return promise;
        }
    }

    // fetch remote
    const ajaxOpts: IAjaxOptions = {
        url,
        method,
        data,
        processData,
        cache: false,
        beforeSend(xhr) {
            // csrf
            if (typeof method !== 'undefined') {
                const verb = method.toUpperCase();

                if (verb !== 'GET' && verb !== 'HEAD' && verb !== 'OPTIONS' && verb !== 'TRACE') {
                    const header = $('meta[name=\'_csrf_header\']').attr('content');
                    const token = $('meta[name=\'_csrf\']').attr('content');
                    if (header && token) {
                        xhr.setRequestHeader(header, token);
                    }
                }
            }
        }
    };

    if (opts.timeout) {
        ajaxOpts.timeout = opts.timeout;
    }

    if (data instanceof FormData) {
        ajaxOpts.contentType = false;
        ajaxOpts.processData = false;
    } else {
        ajaxOpts.dataType = 'json';

        if (processData === false) {
            ajaxOpts.contentType = 'application/json; charset=utf-8';
        }
    }

    // tslint:disable-next-line
    const xhr: JQueryXHR = $.ajax(ajaxOpts);

    // we want fail() handler to be called when !kuura.isValidResponse not just on network error
    const promise: JQueryPromise<any> =
        xhr.then((resp, status, xhr) => {
            if (kuura.isValidResponse(resp)) {
                return $.Deferred().resolve(resp, status, xhr);
            }
            return $.Deferred().reject(resp, status, kuura.getMessage('js_files.msg.notifications.server_error'));
        }, resp => {
            // then() wont fire on network error (404, offline) se we also need this handler
            return $.Deferred().reject(resp, status, kuura.getMessage('js_files.msg.notifications.request_error'));
        }).done(resp => {
            if (cache && method === 'get') {
                Cache.set(cacheKey, resp, cacheGroup, cacheTimeoutS);
            }
        }).fail(resp => {
            if (resp.statusText == 'abort') {
                console.log('Ajax request aborted: - ' + method + ' ' + url);
            } else {
                console.group('Ajax failed - ' + method + ' ' + url);
                console.log('Request data:', data);
                console.log('Response data:', resp);
                console.log('Messages:', getAjaxErrorMessages(resp));
                console.groupEnd();
            }
        });

    (promise as any).abort = () => {
        xhr.abort();
    };

    return promise;
}

/*
 * Moving away from jquery ajax and using some newer Promises based library
 */

const axiosInstance = axios.create({ // use instance because we want to be able not to use the interceptor
    timeout: 20000
})

axiosInstance.interceptors.response.use(response => {
    if (response && response.status === 200 && response.data?.status === false) {
        return Promise.reject(response);
    }
    return response;
}, async error => {
    if (!error.response) {
        return Promise.reject(error);
    }
    const config = error.config;
    if (!config) {
        return Promise.reject(error);
    }

    if (config.useToken && error?.response?.status === 401) {
        if (!config.hasOwnProperty('retry')) config.retry = 0;
        else config.retry += 1;

        if (config.maxRetries !== 0 && config.retry > config.maxRetries) {
            return Promise.reject({ errorMsg: 'retry_exceeded' });
        }

        try {
            const token = await Auth.refreshToken();
            config.headers = { // need to reassign whole headers object, otherwise axios throws 'invalid headers exception'
                ...config.headers,
                Authorization: `Bearer ${token.idToken}`
            };
            return axiosInstance.request(config);
        } catch (error) {
            if ((error?.response?.data?.errorMessage || '').includes('Refresh Token has expired')) {
                Auth.clearToken();
            }
            return Promise.reject(error);
        }
    } else if (error?.response?.status === 410) {
        return Promise.resolve(error?.response?.data);
    }

    // backend usually returns 404 instead of empty array when array is empty
    // so its easier to change this error into success here instead of handling it in every request
    else if (error?.response?.status === 404 && error?.config?.method === 'get') {
        return Promise.resolve([]);
    } else {
        return Promise.reject(error);
    }
});

export async function Ajax2(opts: {
    url: string,
    method?: Method,
    data?: object | number | string,
    params?: object,
    useToken?: boolean,
    maxRetries?: number,
    timeout?: number, // ms
    contentType?: 'urlencoded' | 'json' | 'multipart/form-data',
    responseType?: "blob" | null,
    signal?: AbortSignal,
    headers?: Record<string, string>
}): Promise<any> {
    let {
        data = {},
        method = 'GET',
        useToken = false,
        contentType = 'urlencoded',
        maxRetries = 1,
        headers = {}
    } = opts;

    method = method.toUpperCase() as any;

    const fullHeaders: any = {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': contentType === 'json' ? 'application/json' : 'application/x-www-form-urlencoded',
        ...headers
    };

    // csrf
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
        fullHeaders[$('meta[name="_csrf_header"]').attr('content')] = $('meta[name="_csrf"]').attr('content');
    }

    // token auth
    if (useToken) {
        const token = Auth.getStoredToken();

        if (!token) {
            return Promise.reject({ errorMsg: __('general.validation.unauthorized') });
        }

        fullHeaders.Authorization = 'Bearer ' + token.idToken;
    }

    const axiosOpts: any = {
        headers: fullHeaders,
        ...opts,
        // custom data, to be used in interceptor
        maxRetries,
        retry: 0,
        useToken
    };

    if (method === 'GET') {
        axiosOpts.params = opts.params ?? data;

        if (opts.responseType) {
            axiosOpts.responseType = opts.responseType;
        }
    } else {
        if (data != null) {
            if (contentType === 'json') {
                axiosOpts.data = JSON.stringify(data);
            }
            else if (contentType === 'urlencoded') {
                axiosOpts.data = jQuery.param(data);
            }
            else {
                axiosOpts.data = data;
            }
        } else {
            delete axiosOpts.data;
        }
    }

    return axiosInstance(axiosOpts).then(resp => resp?.data);
}

export function getAjaxErrorMessages(ajaxResponse: any, joiner = '<br>'): string {
    let msg = 'Unknown error';
    if (ajaxResponse && ajaxResponse.errors && Array.isArray(ajaxResponse.errors) && ajaxResponse.errors.length > 0) {
        msg = ajaxResponse.errors.join(joiner);
    }
    return msg;
}

function makeCacheKey(url, data): string {
    let unsafe = url;
    if (data) {
        unsafe += JSON.stringify(data);
    }
    return unsafe.replace(/[/{}:]/gi, '');
}
